// ============================================================================
// VpnPage — «VPN»: ОДНА страница, ДВА режима.
//   • «Подключения» (по умолчанию) — человеческий экран пользователя: только
//     доступные ему подключения, большая кнопка «Подключиться»/«Отключиться»,
//     состояние и срок доступа; QR/файл настроек — в «Расширенных». Никаких
//     технических полей (протокол/сервер/порт/ключи) здесь нет.
//   • «Управление» (только админ или роль «VPN-управление» vpn_manage) —
//     профили, доступ по ролям, проверка соединения, конфиг/QR, журнал сессий,
//     политика протоколов, SSH-доступ (перенесено без изменения поведения).
// SSoT — сервер (VpnService); живое обновление — шина vpn.profile.* /
// vpn.access.* / vpn.settings.*.
// ============================================================================

import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { TabBar } from '@/components/ui/TabBar';
import { canManageVpn, canUseVpn, useSecurityAccess } from '@/lib/securityRoles';
import { VpnConnections } from '@/components/vpn/VpnConnections';
import { VpnManagement } from '@/components/vpn/VpnManagement';

type VpnMode = 'connections' | 'manage';

/** Конфигураторская страница «VPN» открывается сразу в управлении (админу),
 *  приложение в Предприятии — в подключениях. */
function initialMode(canManage: boolean, pathname: string): VpnMode {
  const path = pathname.replace(/\/+$/, '');
  return canManage && path === '/designer/vpn' ? 'manage' : 'connections';
}

export function VpnPage() {
  const access = useSecurityAccess();
  const canManage = canManageVpn(access);
  const canUse = canUseVpn(access);
  const { pathname } = useLocation();
  const [mode, setMode] = useState<VpnMode>(() => initialMode(canManage, pathname));

  const tabs = useMemo(() => ([
    { id: 'connections', label: 'Подключения' },
    ...(canManage ? [{ id: 'manage', label: 'Управление' }] : []),
  ]), [canManage]);

  const activeMode: VpnMode = mode === 'manage' && !canManage ? 'connections' : mode;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3 shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="h-4 w-4 text-primary" /> VPN
        </div>
        {canManage && (
          <TabBar
            tabs={tabs}
            active={activeMode}
            onChange={(id) => setMode(id as VpnMode)}
            group="vpn-mode"
          />
        )}
      </div>

      {activeMode === 'manage' && canManage
        ? <VpnManagement />
        : <VpnConnections canUse={canUse} canManage={canManage} />}
    </div>
  );
}
